import React, { useContext, useEffect } from 'react';
import { EmeraldAiChatBot } from '@emerald-react/ai-chat-bot';
import '@emerald-react/ai-chat-bot/index.esm.css';
import './../layout/text-to-design.css';
import { EmeraldProgressBar } from '@emerald-react/progress-indicator';
import { PromptResponse } from './prompt-response';
import { generateId } from '../../utils/Utils';
import { useApi } from '@backstage/core-plugin-api';
import { promptApiRef } from '../../../apis/promptManagementApi';
import { useUserProfile } from '../../utils/useGetUserProfile';
import { AuthContext } from '../../../providers/AuthProvider';

const encodeCodeSnippetsToBase64 = (input: string) => {
  return input.replace(/```([\s\S]*?)```/g, (match, code) => {
    const encoded = btoa(code);
    return `\`\`\`base64\n${encoded}\n\`\`\``;
  });
};

const TextToPrototype = (props: any) => {
  //const message = `<h1>Welcome to Text to Prototype</h1>`;
  const getHTMLTemplateData = (hideFontStyle = false) => {
    if (props.previewResult?.html) {
      return `<html><head>
                    ${
                      hideFontStyle
                        ? ''
                        : `<style>
              @font-face {
                font-family: 'Calibre-R';
                font-style: normal;
                font-weight: 400;
                src: url('https://docs.emerald.cbre.com/assets/assets/fonts/calibre-r/calibre-r-web-regular.woff2') format('woff2');
                font-display: swap;
              }
              </style>`
                    }
      <style>${
        Array.isArray(props.previewResult.css)
          ? props.previewResult.css.join('\n')
          : props.previewResult.css
      }</style></head><body>${props.previewResult.html}</body></html>`;
    }
    return `<h1>Welcome to Text to Prototype</h1>`;
  };
  const [loading, setLoading] = React.useState(false);
  const [message, setMessage] = React.useState(getHTMLTemplateData());
  const id = generateId();
  const promptApi = useApi(promptApiRef);
  const { accessToken, profile } = useUserProfile();
  const { profEmail, profileName } = useContext(AuthContext);
  const [chatMessage, setChatMessage] = React.useState<any[]>([]);
  const [aiResponse, setAIResponse] = React.useState<string>('');

  let promptResponse: PromptResponse | null = null;
  const getPromptResponse = () => {
    if (!promptResponse) {
      promptResponse = new PromptResponse(
        promptApi,
        profEmail,
        accessToken,
        id,
        '07b6cb62-7f19-48d4-a896-249e449f23a0',
      );
    }
    return promptResponse;
  };

  useEffect(() => {
    if (!promptResponse && promptApi && profEmail && accessToken) {
      promptResponse = getPromptResponse();
      promptResponse.useAIServiceStream = true;
    }
  }, [promptApi, profEmail, profile, accessToken, id]);

  const encodeForAIContext = (code: string) => {
    return code
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  };

  const getInitialPrompt = () => {
    //\`\`\`\`base64\n${btoa(getHTMLTemplateData(true))}\n\`\`\`\`
    //1. Convert the base64 encoded HTML to a readable format before using it.
    return props.previewResult?.html
      ? [
          {
            content: `** CONTEXT:** consider the following HTML content as a starting point for generating the code. The generated code should be well-structured and easy to read. The code should be responsive and compatible with modern web standards. The generated code should not include any external libraries or frameworks. The generated code should be self-contained and not rely on any external resources. The generated code should be optimized for performance and accessibility. The generated code should be compatible with all major web browsers. The generated code should be valid HTML and CSS.
            **INFORMATION ABOUT MY INPUT**
            HTML code: ${props.result?.html}
            CSS code: ${
              Array.isArray(props.result?.css)
                ? props.result?.css.join('\n')
                : props.result?.css
            }
        ** OBJECTIVES ** Use below design token while generating buttons
        1. use color from given design while generating code. Don't use any other colors.
        2. send me the output in readable format, not encoded format.
        `,
            role: 'system',
          },
        ]
      : [
          {
            content: `** CONTEXT:** Generate HTML and integrated CSS code on each HTML tag level inline css for the given text. The generated code should be well-structured and easy to read. The code should be responsive and compatible with modern web standards. The generated code should not include any external libraries or frameworks. The generated code should be self-contained and not rely on any external resources. The generated code should be optimized for performance and accessibility. The generated code should be compatible with all major web browsers. The generated code should be valid HTML and CSS.
        ** OBJECTIVES ** Use below design token while generating buttons
        1. Primary button 'background-color: #003f2d; color: white', for hover 'background-color: rgba(0, 63, 45, 0.08)'
        2. Secondary button 'background-color: #f0f0f0; color: #003f2d', for hover 'background-color: rgba(240, 240, 240, 0.08)'`,
            role: 'system',
          },
        ];
  };
  const getUserPrompt = (message: string) => {
    const _chatMessage = [...chatMessage];
    const response = getHTMLData(true);
    if (aiResponse) {
      _chatMessage.push({
        content: `generated HTML Code: ${response.html} and css code: ${response.css}`, //encodeCodeSnippetsToBase64(aiResponse),
        role: 'assistant',
      });
      setAIResponse('');
    }
    return [
      ..._chatMessage,
      {
        content: message,
        role: 'user',
      },
    ];
  };
  const onClear = () => {
    console.log('Chat cleared');
  };
  const onClose = () => {
    console.log('Chat closed');
  };
  const onMessageCopy = (message: string) => {
    console.log('Message copied:', message);
  };
  const onMessageEdit = (message: string) => {
    console.log('Message edited:', message);
  };
  const onMessageRegenerate = (message: string) => {
    console.log('Message regenerated:', message);
  };
  const onSend = (message: string) => {
    console.log('Message sent:', message);
    promptResponse = getPromptResponse();
    promptResponse.useAIServiceStream = true;
    if (promptResponse) {
      setLoading(true);
      promptResponse.getResponse(
        [...getInitialPrompt(), ...getUserPrompt(message)],
        () => {
          // start
          setMessage(getHTMLData().html);
          setAIResponse('');
          setChatMessage((prev: any[]) => [
            ...prev,
            {
              content: message,
              role: 'user',
            },
          ]);
        },
        () => {
          // stop
          setLoading(false);
          // console.log('AI Response: ', aiResponse);
        },
        (data: any, role?: any) => {
          // onData
          setAIResponse((prev: string) => {
            return prev + (data ? data : '\n');
            //const chatData = appendToChat(prev, data, role);
            //return chatData;
          });
        },
        () => {
          setLoading(false);
          setChatMessage((prev: any[]) => {
            let temp = [...prev];
            if (prev?.[prev.length - 1]?.role === 'assistant') {
              temp = temp.pop();
            }
            return [
              ...temp,
              {
                content: 'Error occured',
                role: 'assistant',
              },
            ];
          });
        },
      );
    }
  };

  const getHTMLData = (forAssistant = false) => {
    const match = aiResponse.match(/```html(.*?)```/s);
    if (match) {
      if (forAssistant) {
        const styleRegex = /<style[^>]*>([\s\S]*?)<\/style>/gi;
        const styles = [];
        let matched;
        while ((matched = styleRegex.exec(match[1].trim())) !== null) {
          styles.push(matched[1].trim());
        }

        // Remove all <style>...</style> blocks from HTML
        //const cleanedHtml = match[1].trim().replace(styleRegex, '');
        return {
          html:
            match[1]
              .trim()
              .match(/<body[^>]*>([\s\S]*?)<\/body>/i)?.[1]
              ?.trim() || '',
          css: styles.join('\n'),
        };
      } else
        return {
          html: match[1].trim(),
          css: '',
        };
    }
    return {
      html: '',
      css: '',
    };
  };
  console.log('AI Response: ', aiResponse);
  console.log('matched code: ', getHTMLData());
  return (
    <div className="text-to-design-layout">
      {loading && (
        <div className="loading-section">
          <EmeraldProgressBar determinate={false} />
        </div>
      )}

      <div className="text-to-design-container">
        <div className="text-to-design-chatbot">
          <EmeraldAiChatBot
            inputProps={{
              placeholder: 'Type a message...',
            }}
            messages={chatMessage}
            onClear={onClear}
            onClose={onClose}
            onMessageCopy={onMessageCopy}
            onMessageEdit={onMessageEdit}
            onMessageRegenerate={onMessageRegenerate}
            onSend={onSend}
            title="Text To Prototype"
          />
        </div>
        <div className="text-to-design-preview">
          <h3>Preview section</h3>
          <iframe
            title="Preview"
            srcDoc={
              getHTMLData(false).html || `<html><body>${message}</body></html>`
            }
            sandbox="allow-scripts"
          />
        </div>
      </div>
    </div>
  );
};
export default TextToPrototype;
